import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { ArrowRight, CheckCircle2, Layers, Server, Cpu, ShieldCheck, Sparkles } from "lucide-react";
import { formatPHP } from "@/lib/format";

const capabilities = [
  {
    title: "Software Solutions",
    description: "Ready-made platforms and custom development tailored to your operations — payroll, documents, facilities, field ops, sales, learning.",
    icon: Layers,
    color: "from-msr-blue/15 to-transparent",
  },
  {
    title: "Hardware Solutions",
    description: "Business desktops, laptops, servers, networking, CCTV, POS, peripherals — supplied, deployed, supported.",
    icon: Cpu,
    color: "from-msr-green/15 to-transparent",
  },
  {
    title: "Infrastructure Services",
    description: "Structured cabling, network setup, server configuration, security, and managed IT for offices and branches.",
    icon: Server,
    color: "from-msr-blue/10 to-transparent",
  },
];

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [solutions, setSolutions] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/solutions", { params: { featured_only: true } }).then((r) => setSolutions(r.data || [])).catch(() => {});
    api.get("/products", { params: { featured_only: true } }).then((r) => setProducts((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-white overflow-hidden border-b border-msr-line">
        <div className="absolute inset-0 grid-bg opacity-60" aria-hidden />
        <div className="absolute -top-20 -right-20 h-[420px] w-[420px] bg-msr-blue/5 rotate-45" aria-hidden />
        <div className="absolute -bottom-32 left-1/2 h-72 w-72 bg-msr-green/5 rotate-12" aria-hidden />

        <div className="msr-container relative grid lg:grid-cols-12 gap-10 py-16 lg:py-28">
          <div className="lg:col-span-7 space-y-7 animate-fade-up">
            <span className="overline" data-testid="hero-overline">
              <span className="mr-2 inline-block h-1.5 w-6 bg-msr-blue" />
              Unlocking New Opportunities
            </span>
            <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-msr-ink leading-[1.05]" data-testid="hero-title">
              Enterprise software, hardware<br />
              and infrastructure for<br />
              <span className="text-msr-blue">Filipino businesses ready to scale.</span>
            </h1>
            <p className="text-lg text-msr-slate max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              {settings?.hero_subtitle ||
                "From payroll automation to document management, facility operations to enterprise hardware — MSR designs, deploys, and supports the technology that runs your business."}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/solutions" className="btn-msr-primary" data-testid="hero-explore-solutions-btn">
                Explore Solutions <ArrowRight size={16} />
              </Link>
              <Link to="/request-quote" className="btn-msr-outline" data-testid="hero-request-quote-btn">
                Request a Quote
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-4 max-w-xl pt-4 border-t border-msr-line">
              <Stat value="8+" label="Ready-made platforms" />
              <Stat value="200+" label="Hardware SKUs" />
              <Stat value="Cloud / On-prem" label="Deployments" />
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div className="absolute -inset-4 border-2 border-msr-blue/20" aria-hidden />
              <div className="absolute -bottom-4 -right-4 h-20 w-20 bg-msr-green" aria-hidden />
              <img
                src={settings?.hero_image || "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg?auto=compress&cs=tinysrgb&w=1600"}
                alt="MSR enterprise technology"
                className="relative w-full h-[420px] lg:h-[520px] object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="bg-msr-surface border-b border-msr-line" data-testid="capabilities-section">
        <div className="msr-container py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="overline mb-3">What we deliver</div>
              <h2 className="font-heading font-extrabold text-3xl lg:text-5xl tracking-tight text-msr-ink">
                Three pillars,<br />one accountable partner.
              </h2>
            </div>
            <p className="lg:col-span-5 text-msr-slate leading-relaxed">
              MSR Business Solutions brings together the software, hardware, and infrastructure expertise required to run modern operations. Engage one team for end-to-end delivery.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {capabilities.map(({ title, description, icon: Icon, color }, i) => (
              <div key={title} className={`msr-card border-t-4 ${i % 2 === 0 ? "border-t-msr-blue" : "border-t-msr-green"}`} data-testid={`capability-${i}`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${color} pointer-events-none`} aria-hidden />
                <div className="msr-corner" aria-hidden />
                <div className="relative">
                  <div className="h-12 w-12 grid place-items-center bg-msr-blue text-white mb-5">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-2">{title}</h3>
                  <p className="text-msr-slate text-sm leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS GRID */}
      <section className="bg-white border-b border-msr-line" data-testid="featured-solutions-section">
        <div className="msr-container py-20">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline mb-3">Ready-made platforms</div>
              <h2 className="font-heading font-extrabold text-3xl lg:text-5xl tracking-tight">MSR Solutions</h2>
            </div>
            <Link to="/solutions" className="btn-msr-ghost" data-testid="home-view-all-solutions-link">
              View all solutions <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {solutions.map((s) => (
              <Link
                key={s.id}
                to={`/solutions/${s.slug}`}
                className="msr-card group block hover:-translate-y-0.5 transition-transform"
                data-testid={`featured-solution-${s.slug}`}
              >
                <div className="msr-corner" aria-hidden />
                <div className="relative h-36 -mx-6 -mt-6 lg:-mx-8 lg:-mt-8 mb-5 overflow-hidden">
                  <img src={resolveImageUrl(s.featured_image)} alt={s.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 badge-soft-blue bg-white/95">{s.category}</div>
                </div>
                <h3 className="font-heading font-extrabold text-xl mb-1.5 text-msr-ink">{s.name}</h3>
                <p className="text-xs text-msr-slate font-medium mb-2 uppercase tracking-wider">{s.full_name}</p>
                <p className="text-sm text-msr-slate leading-relaxed line-clamp-3">{s.short_description}</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-msr-blue text-sm font-semibold">
                  Learn more <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-msr-surface border-b border-msr-line" data-testid="featured-products-section">
        <div className="msr-container py-20">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline mb-3">From the MSR Shop</div>
              <h2 className="font-heading font-extrabold text-3xl lg:text-5xl tracking-tight">Hardware, Software & Bundles</h2>
            </div>
            <Link to="/shop" className="btn-msr-ghost" data-testid="home-view-all-products-link">
              Visit Shop <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => (
              <Link key={p.id} to={`/shop/${p.slug}`} className="msr-card group hover:-translate-y-0.5 transition-transform block" data-testid={`featured-product-${p.slug}`}>
                <div className="relative h-44 -mx-6 -mt-6 lg:-mx-8 lg:-mt-8 mb-5 overflow-hidden bg-msr-mist">
                  <img src={resolveImageUrl(p.featured_image)} alt={p.name} className="h-full w-full object-cover" />
                  {p.compare_at_price > p.price && (
                    <span className="absolute top-3 right-3 bg-msr-green text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">Save</span>
                  )}
                  <span className="absolute bottom-3 left-3 badge-soft-blue bg-white/95">{p.product_type}</span>
                </div>
                <h3 className="font-heading font-bold text-lg leading-snug">{p.name}</h3>
                <p className="text-sm text-msr-slate mt-2 mb-4 line-clamp-2">{p.short_description}</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-heading font-extrabold text-xl text-msr-blue">{formatPHP(p.price)}</span>
                  {p.compare_at_price > p.price && (
                    <span className="text-xs text-msr-slate line-through">{formatPHP(p.compare_at_price)}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST/WHY MSR */}
      <section className="relative bg-msr-ink text-white overflow-hidden" data-testid="trust-section">
        <div className="absolute -top-24 -left-24 h-96 w-96 border-2 border-msr-blue/30 rotate-12" aria-hidden />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 border-2 border-msr-green/30 -rotate-12" aria-hidden />
        <div className="msr-container py-20 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-5">
            <div className="overline text-msr-green-light mb-3">Why MSR</div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-5xl tracking-tight">
              Filipino-built,<br /> enterprise-grade.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              We design for the operational realities of Philippine businesses — BIR compliance, multi-branch operations, cloud or on-premise, with the service partner you can call.
            </p>
            <Link to="/about" className="mt-7 inline-flex items-center gap-1.5 text-msr-green-light font-semibold hover:text-white" data-testid="trust-about-link">
              Read more about MSR <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { icon: CheckCircle2, title: "Compliance-first", text: "BIR, SSS, PhilHealth, Pag-IBIG built-in." },
              { icon: ShieldCheck, title: "Secure deployments", text: "Cloud or on-premise with role-based access." },
              { icon: Sparkles, title: "Ready in days", text: "Battle-tested platforms with proven roll-outs." },
              { icon: Layers, title: "Full-stack partner", text: "Software, hardware, and infrastructure under one team." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="p-6 bg-white/[0.04] border border-white/10" data-testid={`trust-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={22} className="text-msr-green-light mb-3" />
                <div className="font-heading font-bold mb-1">{title}</div>
                <div className="text-sm text-white/70">{text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-white" data-testid="home-cta-section">
        <div className="msr-container py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-3">Have a project in mind?</div>
            <h2 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight">
              Tell us what you need.<br />We'll prepare a tailored proposal.
            </h2>
            <p className="mt-3 text-msr-slate max-w-2xl">
              Submit your hardware, software, or infrastructure RFQ. Our team typically responds with a tailored proposal within 3 working days.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link to="/request-quote" className="btn-msr-primary" data-testid="home-cta-rfq-btn">Request a Quote</Link>
            <Link to="/contact" className="btn-msr-outline" data-testid="home-cta-contact-btn">Book a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-heading font-extrabold text-2xl text-msr-blue">{value}</div>
      <div className="text-xs text-msr-slate uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}
